import { memo, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Workbook, WorkbookInstance } from '@fortune-sheet/react';
import type { Op, Hooks } from '@fortune-sheet/core';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { BottomDrawer } from '../BottomDrawer/BottomDrawer';
import Button from '../_custom/Button/Button';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { useIsMobile } from '../_custom/Hooks/useIsMobile';
import { useIsMobileOrTabletDevice } from '../_custom/Hooks/useIsMobileOrTabletDevice';
import { useCloseOnBack } from '../_custom/Hooks/useCloseOnBack';
import CompactDownloadButton from '../DocumentViewer/components/CompactDownloadButton/CompactDownloadButton';
import { useSheetEditorDrawerLogic, SavingPhase } from './useSheetEditorDrawerLogic';
import { useSheetEditorDialogs } from './useSheetEditorDialogs';
import { useSheetSelectionStatus } from './excel/useSheetSelectionStatus';
import { useSheetToolbarItems } from './excel/useSheetToolbarItems';
import type { SheetToolbarItems } from './excel/useSheetToolbarItems.types';
import { getInternals, commitEditThen } from './excel/workbookInternals';
import { FortuneSheet } from './types';
import { SheetStorageRef } from './sheetStorage';
import { DEFAULT_COL_WIDTH, MIN_ROW_HEIGHT } from './fileTypes';
import { useColumnAutofit } from './useColumnAutofit';
import { useSheetTabWheelScroll } from './useSheetTabWheelScroll';
import { useWorkbookResizeSync } from './useWorkbookResizeSync';
import { ensureSheetFontList, ensureExcelNumberPresets } from './fortuneSheetSetup';
import {
    tooLargeToOpenMessage,
    readOnlyAddMessage,
    readOnlyNoticeMessage,
    readOnlySaveMessage,
    LARGE_FILE_NOTICE,
    READ_ONLY_DOWNLOAD_UNAVAILABLE,
    READ_ONLY_HISTORY_UNAVAILABLE,
} from './sheetSizeGuard';
import { useSheetWheel } from './excel/useSheetWheel';
import { useSheetFocus } from './excel/useSheetFocus';
import { useSheetKeyboardShortcuts } from './excel/useSheetKeyboardShortcuts';
import { useKeyTips } from './excel/useKeyTips';
import KeyTipsOverlay from './excel/KeyTipsOverlay';
import GoToDialog from './excel/GoToDialog';
import NameBox from './excel/NameBox';
import StatusBar from './excel/StatusBar';
import FormatCellsDialog from './excel/FormatCellsDialog';
import PasteSpecialDialog from './excel/PasteSpecialDialog';
import { RUPEE_SYMBOL } from './excel/numberFormats';
import { useOutsideClick } from '../../utils/constants/hooks';
import { editIconPurple, commentsIcon, ClockCounterDark } from '../../assets/hostedassets';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import RadioButton from '../RadioButton/RadioButton';
import RenameReportDialog from '../../modules/Reports/components/RenameReportDialog/RenameReportDialog';
import WhiteCheckIcon from '../Reports/WhiteCheckIcon';
import HistorySideDrawer from '../HistorySideDrawer/HistorySideDrawer';
import UnsavedEditsDialog from '../DocumentViewer/UnsavedEditsDialog';
import WorkspaceSyncStatus from '../DocumentViewer/WorkspaceSyncStatus';
import { useUnsavedEditsGuard } from '../DocumentViewer/useUnsavedEditsGuard';
import type { AddToWorkspacePayload } from '../Reports/addFileToReports';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { resetAndHideCommentsThread, closeCommentsThread, setShowCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import { COMMENTS_THREAD_SOURCES } from '../../store/commentsThread/commentsThread_reducer';
import styles from './SheetEditorDrawer.module.scss';
import '@fortune-sheet/react/dist/index.css';
import './fortuneSheetOverrides.css';

const HISTORY_PORTAL_TARGET_ID = 'sheet-editor-history';
const COMMENTS_PORTAL_TARGET_ID = 'reports-editor-comments';

// Extend the toolbar font list and Number-format presets once, before any
// <Workbook> mounts.
ensureSheetFontList();
ensureExcelNumberPresets();

export interface SheetEditorDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Original file name; used as the drawer title and download base name. */
    fileName: string;
    /** In-memory sheets (import / composed). When provided, renders directly — skips URL fetch. */
    sheets?: FortuneSheet[];
    /** Presigned URL of the xlsx/csv file to edit (legacy fetch path). */
    fileUrl?: string;
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Existing saved-document id — passed through to the save payload for update. */
    docId?: string;
    /** Server-assigned generation-lineage id — carried through so a new version
     *  saved from this drawer groups with its siblings (mirrors the Plate
     *  editor's persist path in ReportEditorRoute). */
    genId?: string;
    /** A device import opens the drawer before its own eager READY save
     *  resolves a docId — this promise (Reports' own handleImportFile)
     *  resolves to that save's {docId, genId, storage} on success, or null
     *  once it's clear none is coming. Absent for every other open path. */
    pendingDocIdPromise?: Promise<{ docId: string; genId?: string; storage?: SheetStorageRef } | null>;
    /** Set ONLY for a blank "New Spreadsheet" — see
     *  useSheetEditorDrawerLogic's own doc comment for what it gates. */
    isBlankNewSheet?: boolean;
    /** Save context. When absent (e.g. the read-only DataRoom viewer) the Save
     *  action is hidden and the drawer is view + download only. */
    assesseeOrgId?: string;
    investorOrgId?: string;
    createdBy?: string;
    documentType?: string;
    name?: string;
    description?: string;
    /** S3 storage ref from a previously saved document — enables version continuity. */
    storage?: SheetStorageRef;
    /** The opened document's status ('DRAFT' | 'READY') — a draft-preferred open
     *  hands the drawer a live autosave draft, which Save & Close must still
     *  commit even when this session made no edits. */
    status?: string;
    /** Called with the saved document after a successful save (`meta.closing`
     *  tells the caller whether this save should close the drawer). */
    onSaved?: (doc: any, meta?: { closing: boolean }) => void;
    /** Called with the saved document after a name/description edit succeeds.
     *  Unlike `onSaved` this does not close the drawer — only the caller's own
     *  list (e.g. the Reports table) needs refreshing; the drawer's header
     *  already reflects the new name. */
    onRenamed?: (doc: any) => void;
    /** Snapshot this sheet into Reports (Ask AICA / Data Room view contexts).
     *  When provided, this becomes the PRIMARY header action instead of Save —
     *  a view context never has both (you don't add a report to reports).
     *  Receives the read-only band's original bytes for a size-capped
     *  workbook, the EDITED on-screen sheets when the user changed anything,
     *  or `undefined` when the workbook is untouched (→ snapshot the original). */
    onAddToReports?: (payload?: AddToWorkspacePayload) => void;
    /** Loading state for the Add to Reports action, controlled by the caller. */
    addingToReports?: boolean;
    /** Briefly true right after a successful add — swaps the button to a
     *  checkmark + "Added" state, controlled by the caller. */
    addedToReports?: boolean;
    /** True while the caller is still preparing the sheets to hand in (e.g.
     *  parsing an imported file) — shows the "Preparing spreadsheet…" loader
     *  instead of resolving `sheets`/`fileUrl` against an empty/undefined value. */
    preparing?: boolean;
    /** Read-only preview: no toolbar, no editing, no persistence, no Add to Workspace. */
    viewOnly?: boolean;
    /** True once an in-progress DEVICE IMPORT's parse has been classified
     *  'large' (500k-2M cells) — ORed with the fetch path's own
     *  `largeNotice` (from `useResolveSheets`) so both loading paths show the
     *  same "may take a few seconds" notice, not just opening an existing report. */
    largeNotice?: boolean;
    /** The file's original bytes, passed ONLY when the workbook is in the
     *  read-only band (more filled cells than can be edited) — Download, Save
     *  and Add then store these as-is instead of re-serialising the workbook. */
    originalBytes?: ArrayBuffer;
}

const SAVE_PHASE_LABELS: Record<SavingPhase, string> = {
    idle: 'Save and Close',
    serializing: 'Preparing…',
    uploading: 'Uploading…',
    saving: 'Saving…',
};

// Fortune-Sheet's default toolbar with the one-click currency button removed.
// Currency formatting is still reachable via the "format" dropdown; we drop the
// quick button per product (no multi-currency need). RUPEE_SYMBOL makes the
// Format dropdown default to the Indian Rupee instead of the library default (¥).
const TOOLBAR_ITEMS = [
    'undo',
    'redo',
    'format-painter',
    'clear-format',
    '|',
    'percentage-format',
    'number-decrease',
    'number-increase',
    'format',
    '|',
    'font',
    '|',
    'font-size',
    '|',
    'bold',
    'italic',
    'strike-through',
    'underline',
    '|',
    'font-color',
    'background',
    'border',
    'merge-cell',
    '|',
    'horizontal-align',
    'vertical-align',
    'text-wrap',
    'text-rotation',
    '|',
    'freeze',
    'conditionFormat',
    'filter',
    'link',
    'image',
    'comment',
    'quick-formula',
    'dataVerification',
    'splitColumn',
    'locationCondition',
    'screenshot',
    'search',
];

interface SheetWorkbookProps {
    data: FortuneSheet[];
    workbookRef: React.RefObject<WorkbookInstance>;
    customToolbarItems: SheetToolbarItems;
    allowEdit: boolean;
    showToolbar: boolean;
    onOp: (ops: Op[]) => void;
    hooks: Hooks;
}

/**
 * The <Workbook> subtree, isolated behind React.memo and given ONLY the props
 * it actually needs. Fortune-Sheet's `mergedSettings` memo does a full
 * `cloneDeep` + re-runs its mount/init effect on ANY new prop identity — so
 * header/pane state (downloadMenuOpen, renameOpen, autosaveStatus, history/
 * comments toggles, name edits) must never reach this component, or every one
 * of those unrelated re-renders would re-init the whole grid.
 */
const SheetWorkbook = memo(function SheetWorkbook({
    data,
    workbookRef,
    customToolbarItems,
    allowEdit,
    showToolbar,
    onOp,
    hooks,
}: SheetWorkbookProps) {
    return (
        <Workbook
            ref={workbookRef}
            data={data}
            toolbarItems={TOOLBAR_ITEMS}
            customToolbarItems={customToolbarItems}
            lang="en"
            currency={RUPEE_SYMBOL}
            showToolbar={showToolbar}
            showFormulaBar
            showSheetTabs
            allowEdit={allowEdit}
            defaultRowHeight={MIN_ROW_HEIGHT}
            defaultColWidth={DEFAULT_COL_WIDTH}
            onOp={onOp}
            hooks={hooks}
        />
    );
});

/**
 * Editable spreadsheet drawer. Accepts either in-memory FortuneSheet[] (from
 * import or compose) or a presigned URL to fetch. Renders a Fortune-Sheet
 * grid (formulas + formula bar built in) inside the same BottomDrawer the
 * PlateEditor uses. Header actions: one "Download ▾" dropdown (.xlsx/.csv),
 * plus a primary action — "Add to Reports" when `onAddToReports` is passed
 * (view contexts), else "Save" when `canSave` (Reports edit/import).
 */
const SheetEditorDrawer: React.FC<SheetEditorDrawerProps> = ({
    open,
    onClose,
    fileName,
    sheets: inMemorySheets,
    fileUrl,
    mimeType,
    docId,
    genId,
    pendingDocIdPromise,
    isBlankNewSheet,
    assesseeOrgId,
    investorOrgId,
    createdBy,
    documentType,
    name,
    description,
    storage,
    status,
    onSaved,
    onRenamed,
    onAddToReports,
    addingToReports,
    addedToReports,
    preparing,
    viewOnly = false,
    largeNotice: importLargeNotice,
    originalBytes,
}) => {
    const isMobile = useIsMobile();
    const isMobileOrTablet = useIsMobileOrTabletDevice();
    // A view-only preview drops the persistence context: no Save/Rename/Comments/History.
    const persistenceOrgId = viewOnly ? undefined : assesseeOrgId;

    const {
        canSave,
        sheets,
        workbookSheets,
        loading,
        loadingStage,
        error,
        tooLarge,
        largeNotice,
        saving,
        savingPhase,
        autosaveStatus,
        workbookRef,
        handleDownloadXlsx,
        handleDownloadCsv,
        handleSave,
        hasPendingChanges,
        closeFailedOpen,
        setCloseFailedOpen,
        commitEditAndClose,
        handleRetryCloseFailed,
        handleCloseWithoutSaving,
        markWorkbookDirty,
        hasUnsavedEdits,
        hasEditedSinceOpen,
        getEditVersion,
        clearUnsavedEdits,
        getCurrentSheets,
        currentName,
        currentDescription,
        handleRename,
        hasGenId,
        historyOpen,
        historyExpanded,
        historyLoading,
        historyEntries,
        historyPreviewLoading,
        toggleHistoryDrawer,
        closeHistoryDrawer,
        toggleHistoryExpand,
        previewHistoryEntry,
        selectHistoryEntry,
        previewEntryId,
        previewActive,
        restoreVersion,
        sizeReadOnly,
        filledCells,
        canPersistReadOnly,
        originalIsXlsx,
        canDownloadOriginal,
        readOnlyAddBand,
        handleDownloadOriginal,
    } = useSheetEditorDrawerLogic({
        open,
        fileName,
        sheets: inMemorySheets,
        fileUrl,
        mimeType,
        docId,
        genId,
        pendingDocIdPromise,
        isBlankNewSheet,
        preparing,
        assesseeOrgId: persistenceOrgId,
        investorOrgId,
        createdBy,
        documentType,
        name,
        description,
        storage,
        status,
        onSaved,
        onRenamed,
        onClose,
        originalBytes,
    });
    // A history preview, a size-capped workbook or a view-only preview mounts
    // the grid read-only.
    const editLocked = historyOpen || sizeReadOnly || viewOnly;
    // Mobile/tablet Back closes through the same commit-then-flush path as ×/backdrop/idle-Esc.
    useCloseOnBack(open && isMobileOrTablet, commitEditAndClose);

    // View contexts (no save context) can't autosave, so an edited workbook
    // only reaches Workspace through the header's Add to Workspace — the guard
    // hands that action the live sheets and protects the close paths. The
    // Reports context (canSave) autosaves as a DRAFT and flushes on unmount,
    // so it needs neither; a view-only preview can't be edited at all.
    const workspaceGuard = useUnsavedEditsGuard({
        enabled: !canSave && !viewOnly,
        hasUnsavedEdits,
        hasEditedSinceOpen,
        getEditVersion,
        buildSnapshot: () => ({ kind: 'sheet', sheets: getCurrentSheets() }),
        clearUnsavedEdits,
        // A confirmed close still commits any in-progress cell edit and
        // flushes, exactly like an unguarded ×/backdrop/idle-Esc.
        onClose: commitEditAndClose,
        onAddToReports,
        addingToReports,
        addedToReports,
    });

    const dispatch = useAppDispatch();
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    // Scoped to THIS drawer's source — another surface's open thread must not
    // light this button or slide open an empty pane here (mirrors PlateDocumentDrawer).
    const commentsActive = Boolean(
        canSave &&
            commentsThreadState?.showCommentsThread &&
            !commentsThreadState?.closingCommentsThread &&
            commentsThreadState?.source === COMMENTS_THREAD_SOURCES.REPORTS,
    );
    const commentsExpanded = commentsActive && Boolean(commentsThreadState?.expandCommentsThread);

    const toggleComments = useCallback(() => {
        if (commentsActive) {
            dispatch(closeCommentsThread());
            return;
        }
        if (!canSave || !persistenceOrgId) return;
        // Mutual exclusion with the history pane, in the other direction below.
        if (historyOpen) closeHistoryDrawer();
        dispatch(setShowCommentsThread({ source: COMMENTS_THREAD_SOURCES.REPORTS, assesseeOrgId: persistenceOrgId }));
    }, [commentsActive, canSave, persistenceOrgId, dispatch, historyOpen, closeHistoryDrawer]);

    const handleHistoryToggle = useCallback(() => {
        if (commentsActive) dispatch(closeCommentsThread());
        toggleHistoryDrawer();
    }, [commentsActive, dispatch, toggleHistoryDrawer]);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
        return () => {
            if (canSave) dispatch(resetAndHideCommentsThread());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const { fitColumnsToolbarItem } = useColumnAutofit({ workbookRef, wrapperRef, enabled: !loading && !error && !!sheets?.length });

    const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
    const downloadMenuRef = useRef<HTMLDivElement>(null);
    useOutsideClick(downloadMenuRef, () => setDownloadMenuOpen(false));

    const {
        goToOpen,
        setGoToOpen,
        formatCellsOpen,
        setFormatCellsOpen,
        deleteInsertMode,
        openDeleteInsert,
        deleteInsertAxis,
        setDeleteInsertAxis,
        renameOpen,
        setRenameOpen,
        pasteSpecial,
        anyDialogOpen,
        closeAnyDialog,
        isNameBoxTarget,
        applyDeleteInsert,
    } = useSheetEditorDialogs({
        open,
        workbookRef,
        historyOpen: editLocked,
        closeFailedOpen,
        setCloseFailedOpen,
    });
    const { selectionTick, statusBarNotifyRef, hooks, handleOp } = useSheetSelectionStatus({ markWorkbookDirty });
    const onOpenFormatCells = useCallback(() => setFormatCellsOpen(true), [setFormatCellsOpen]);
    const onOpenGoTo = useCallback(() => setGoToOpen(true), [setGoToOpen]);
    const customToolbarItems = useSheetToolbarItems({ fitColumnsToolbarItem, onOpenFormatCells, readOnly: sizeReadOnly });

    const saveButtonLabel = SAVE_PHASE_LABELS[savingPhase];

    // Every close/save path below that is NOT an Esc-cancel must commit
    // any in-progress cell edit first (same mechanism saveShortcut's Ctrl+S
    // uses) — otherwise typed-but-uncommitted text is silently dropped. Esc
    // itself is untouched (rule 3 already cancels via the library's own
    // Esc handling before ever reaching onClose). The ×/backdrop/idle-Esc
    // close/flush/retry orchestration itself lives in useSheetCloseFlow
    // (composed by useSheetEditorDrawerLogic above) — this one remaining
    // local use is the Save button's own commit-then-save-then-close.
    const commitEditThenRun = useCallback((then: () => void) => commitEditThen(getInternals(workbookRef.current), then), [workbookRef]);

    // Read-only band: Add needs the original .xlsx; Save / Rename need stored
    // bytes or the original .xlsx (a .csv/.xls is never re-serialised).
    const addBlockedByReadOnly = sizeReadOnly && !originalIsXlsx;
    const saveBlockedByReadOnly = sizeReadOnly && !canPersistReadOnly;

    const headerRight =
        !loading && !error && !tooLarge ? (
            <div className={styles.actions}>
                {!canSave && !viewOnly && <WorkspaceSyncStatus status={workspaceGuard.syncStatus} />}
                {canSave && autosaveStatus !== 'idle' && (
                    <div className={styles.headerSyncStatus} aria-live="polite">
                        {autosaveStatus === 'saving' ? (
                            <>
                                <SyncRoundedIcon className={clsx(styles.syncIcon, styles.syncIconSpin)} fontSize="small" />
                                <span>Saving</span>
                            </>
                        ) : (
                            <>
                                <svg viewBox="-1 -1 26 26" className={styles.syncIcon} aria-hidden fill="none">
                                    <path
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
                                    />
                                    <path fill="currentColor" d="M10 14.18L7.91 12.09L6.5 13.5L10 17L16.01 10.99L14.60 9.58Z" />
                                </svg>
                                <span>{autosaveStatus === 'error' ? 'Save failed' : 'Saved'}</span>
                            </>
                        )}
                    </div>
                )}
                {canSave && (
                    <button
                        type="button"
                        className={clsx(styles.historyBtn, commentsActive && styles.historyBtnActive)}
                        aria-label="Toggle comments"
                        title="Comments"
                        onClick={toggleComments}
                        disabled={!persistenceOrgId}
                    >
                        <img src={commentsIcon} alt="" aria-hidden className={styles.btnIcon} />
                    </button>
                )}
                {canSave && (
                    <button
                        type="button"
                        className={clsx(styles.historyBtn, historyOpen && styles.historyBtnActive)}
                        aria-label="View history"
                        title={sizeReadOnly ? READ_ONLY_HISTORY_UNAVAILABLE : 'Version history'}
                        onClick={handleHistoryToggle}
                        disabled={!hasGenId || sizeReadOnly}
                    >
                        <img src={ClockCounterDark} alt="" aria-hidden className={styles.btnIcon} />
                    </button>
                )}
                {sizeReadOnly && (
                    <span className={styles.readOnlyPill} title={readOnlyNoticeMessage(filledCells)}>
                        Read-only
                    </span>
                )}
                {sizeReadOnly ? (
                    <button
                        type="button"
                        className={styles.downloadBtn}
                        onClick={handleDownloadOriginal}
                        disabled={!canDownloadOriginal}
                        title={canDownloadOriginal ? undefined : READ_ONLY_DOWNLOAD_UNAVAILABLE}
                    >
                        Download
                    </button>
                ) : (
                    <div className={styles.downloadGroup} ref={downloadMenuRef}>
                        {isMobile ? (
                            <CompactDownloadButton onClick={() => setDownloadMenuOpen((o) => !o)} expanded={downloadMenuOpen} />
                        ) : (
                            <button
                                type="button"
                                className={styles.downloadBtn}
                                onClick={() => setDownloadMenuOpen((o) => !o)}
                                aria-haspopup="true"
                                aria-expanded={downloadMenuOpen}
                            >
                                Download <span className={styles.downloadCaret}>▾</span>
                            </button>
                        )}
                        {downloadMenuOpen && (
                            <div className={styles.downloadPopover} role="menu" aria-label="Download options">
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={styles.downloadPopoverItem}
                                    onClick={() => {
                                        setDownloadMenuOpen(false);
                                        handleDownloadXlsx();
                                    }}
                                >
                                    Download as .xlsx
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={styles.downloadPopoverItem}
                                    onClick={() => {
                                        setDownloadMenuOpen(false);
                                        handleDownloadCsv();
                                    }}
                                >
                                    Download as .csv
                                </button>
                            </div>
                        )}
                    </div>
                )}
                {!viewOnly && onAddToReports ? (
                    <span className={styles.tooltipHost} title={addBlockedByReadOnly ? readOnlyAddMessage(filledCells) : undefined}>
                        <Button
                            type="button"
                            text={addedToReports ? <WhiteCheckIcon /> : 'Add to Workspace'}
                            variant="purple"
                            className={styles.headerBtn}
                            // A size-capped workbook can't be edited, so its band (original
                            // bytes) and an edited snapshot never compete for this click.
                            onClick={
                                addedToReports
                                    ? () => {}
                                    : readOnlyAddBand
                                    ? () => onAddToReports(readOnlyAddBand)
                                    : workspaceGuard.addToWorkspace
                            }
                            disabled={addingToReports || addBlockedByReadOnly}
                            loading={!addedToReports && addingToReports}
                        />
                    </span>
                ) : (
                    canSave && (
                        <span className={styles.tooltipHost} title={saveBlockedByReadOnly ? readOnlySaveMessage(filledCells) : undefined}>
                            <Button
                                type="button"
                                text={saveButtonLabel}
                                variant="purple"
                                className={styles.headerBtn}
                                onClick={() =>
                                    commitEditThenRun(async () => {
                                        // Close only on a confirmed save — a failed save keeps the
                                        // drawer (and the user's unsaved edits) open.
                                        if (await handleSave()) onClose();
                                    })
                                }
                                disabled={saving || historyOpen || saveBlockedByReadOnly}
                                loading={saving}
                            />
                        </span>
                    )
                )}
            </div>
        ) : null;

    // Use fileUrl as the workbook key on the fetch path; fall back to fileName
    // so in-memory sheets get a stable key that doesn't remount on every render.
    // A history preview (or a restore, even without a prior preview) forces a
    // remount — fortune-sheet only reads `data` at init. Keyed on previewActive
    // (content actually swapped), NOT previewEntryId: opening the pane
    // auto-selects the latest row without changing content, and remounting
    // read-only there is both a large-workbook stall and the crash-prone path
    // (see handleRestoreVersion's comment on the nested-update loop).
    const workbookKey = `${fileUrl ?? fileName}::${previewActive ? previewEntryId : 'live'}::${restoreVersion}`;

    // Report save-context (Reports table open/import) gets an editable title —
    // same "name + purple Edit pill" markup PlateDocumentDrawer uses for Reports.
    // Other contexts (Data Room, Ask AICA) keep the plain file name.
    const displayName = currentName || fileName;
    const drawerTitle = canSave ? (
        <div className={styles.headerLeft}>
            <div className={styles.titleBlock}>
                <span className={styles.titleText} title={displayName}>
                    {displayName}
                </span>
                <div
                    className={clsx(styles.editPill, (historyOpen || saveBlockedByReadOnly) && styles.editPillDisabled)}
                    onClick={() => !historyOpen && !saveBlockedByReadOnly && setRenameOpen(true)}
                    role="button"
                    tabIndex={0}
                    aria-label="Edit name and description"
                    aria-disabled={historyOpen || saveBlockedByReadOnly}
                    title={saveBlockedByReadOnly ? readOnlySaveMessage(filledCells) : 'Edit'}
                    onKeyDown={(e) => {
                        if (!historyOpen && !saveBlockedByReadOnly && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            setRenameOpen(true);
                        }
                    }}
                >
                    <img src={editIconPurple} alt="" aria-hidden className={styles.editPillIcon} />
                    <span>Edit</span>
                </div>
            </div>
        </div>
    ) : (
        fileName
    );

    // `preparing` (import: file still being parsed) holds the loader up even
    // once useResolveSheets has already settled to its empty-fallback blank
    // sheet — the caller hands sheets in once parsing finishes.
    const effectiveLoading = loading || Boolean(preparing);
    const loadingStatusText = preparing
        ? 'Preparing spreadsheet…'
        : loadingStage === 'downloading'
        ? 'Downloading file…'
        : loadingStage === 'parsing'
        ? 'Preparing spreadsheet…'
        : null;
    // Shown alongside the loading text AND through the post-load paint overlay
    // below (largeNotice only resets at the START of the next resolve, so it
    // stays true across that brief window) — non-blocking, nothing under 500k cells.
    const largeFileNoticeText = largeNotice || importLargeNotice ? LARGE_FILE_NOTICE : null;

    // workbookSheets already reflects whichever content is showing — a history
    // preview (read-only) or the live sheets — both lazy-hydrated the same way.
    const dataForWorkbook = workbookSheets;

    // Keeps the "Preparing spreadsheet…" overlay up through the Workbook's
    // first commit + first paint (not just until sheets resolve) — a large
    // workbook still blocks for a beat mounting/painting after `data` is ready.
    // Re-arms on every remount (workbookKey change: initial load, import, or a
    // history preview/restore swap) so each one gets the same brief cover.
    const [paintedKey, setPaintedKey] = useState<string | null>(null);
    useEffect(() => {
        if (effectiveLoading || error || !dataForWorkbook) return undefined;
        let raf1 = 0;
        let raf2 = 0;
        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => setPaintedKey(workbookKey));
        });
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
    }, [effectiveLoading, error, dataForWorkbook, workbookKey]);
    const showPaintOverlay = !effectiveLoading && !error && !!dataForWorkbook && paintedKey !== workbookKey;

    const workbookMounted = !effectiveLoading && !error && !!dataForWorkbook;
    useSheetTabWheelScroll(wrapperRef, workbookKey, workbookMounted);
    useWorkbookResizeSync(wrapperRef, workbookMounted, workbookKey);

    // Excel-parity keyboard/focus/wheel layer, active in every mount; only
    // the read-only history preview blocks edits.
    const { refocusAndRedispatch, focusCellInput } = useSheetFocus({
        wrapperRef,
        paintedKey,
        workbookKey,
        dialogOpen: anyDialogOpen,
        workbookMounted,
        workbookRef,
        // A history preview is read-only — the focus-lost fallback's
        // "type to replace" path must not start an edit on it, same as
        // every other mutating lane (the Workbook allowEdit prop below).
        allowEdit: !editLocked,
    });
    useSheetWheel({ wrapperRef, workbookRef, workbookMounted });
    // Lane K is wired before Lane B below, so its document
    // listener is registered first and can consume a key before Lane B ever
    // sees it (react effect/listener registration order).
    const {
        keytipsOpen,
        targets: keyTipTargets,
        announce: keyTipsAnnounce,
        closeLevel: closeKeytipsLevel,
    } = useKeyTips({
        open,
        wrapperRef,
        workbookRef,
        historyOpen: editLocked,
        dialogOpen: anyDialogOpen,
        focusCellInput,
    });
    const { handleKeyDownCapture, handleDoubleClickCapture } = useSheetKeyboardShortcuts({
        workbook: { open, workbookMounted, workbookRef, wrapperRef, historyOpen, canSave, readOnly: editLocked },
        gates: { hasPendingChanges, handleDownloadXlsx },
        dialogs: {
            dialogOpen: anyDialogOpen,
            onCloseDialog: closeAnyDialog,
            isNameBoxTarget,
            onOpenGoTo,
            onOpenFormatCells,
            onArmValuesPaste: pasteSpecial.armValuesPaste,
            onOpenPasteSpecial: pasteSpecial.openFromKeyboard,
            onOpenDeleteInsert: openDeleteInsert,
            keytipsOpen,
            onCloseKeytipsLevel: closeKeytipsLevel,
        },
        actions: {
            // The idle-Esc close-drawer outcome must also flush a pending
            // autosave before closing — Esc while editing is unaffected
            // (pass-through happens before ever reaching this).
            onClose: commitEditAndClose,
            // Ctrl/Cmd+S saves without closing the drawer — tagging the
            // source lets Reports' onSaved tell the difference from Save & Close,
            // which calls handleSave() with no args (below, unchanged).
            handleSave: () => handleSave({ source: 'shortcut' }),
            refocusAndRedispatch,
            focusCellInput,
        },
    });

    return (
        <>
            <BottomDrawer
                open={open}
                onClose={workspaceGuard.requestClose}
                title={drawerTitle}
                headerRight={headerRight}
                className={styles.drawer}
                bodyClassName={styles.body}
                height="100vh"
                closeOnEscape={false}
                rightSlot={
                    canSave ? (
                        <>
                            <aside className={clsx(styles.historyPane, historyOpen && styles.historyPaneOpen)} aria-hidden={!historyOpen}>
                                <div id={HISTORY_PORTAL_TARGET_ID} className={styles.historyPortalTarget} />
                            </aside>
                            <aside
                                className={clsx(
                                    styles.commentsPane,
                                    commentsActive && styles.commentsPaneOpen,
                                    commentsExpanded && styles.commentsPaneExpanded,
                                )}
                                aria-hidden={!commentsActive}
                            >
                                <div id={COMMENTS_PORTAL_TARGET_ID} className={styles.commentsPortalTarget} />
                            </aside>
                        </>
                    ) : undefined
                }
            >
                {effectiveLoading && (
                    <div className={styles.statusBox}>
                        <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
                        {loadingStatusText && <p className={styles.statusLine}>{loadingStatusText}</p>}
                        {largeFileNoticeText && <p className={styles.statusLine}>{largeFileNoticeText}</p>}
                    </div>
                )}
                {tooLarge && (
                    <div className={styles.statusBox}>
                        <p>{tooLargeToOpenMessage(tooLarge.cells, tooLarge.basis)}</p>
                        <Button type="button" text="Download" variant="purple" onClick={handleDownloadOriginal} />
                    </div>
                )}
                {error && !tooLarge && (
                    <div className={styles.statusBox}>
                        <p>Unable to open this file for editing.</p>
                    </div>
                )}
                {!effectiveLoading && !error && !tooLarge && dataForWorkbook && (
                    <div
                        className={styles.workbookWrapper}
                        ref={wrapperRef}
                        data-aica-sheet-editor
                        data-aica-toolbar-wrap
                        onKeyDownCapture={handleKeyDownCapture}
                        onDoubleClickCapture={handleDoubleClickCapture}
                    >
                        {showPaintOverlay && (
                            <div className={styles.paintOverlay}>
                                <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
                                <p className={styles.statusLine}>Preparing spreadsheet…</p>
                                {largeFileNoticeText && <p className={styles.statusLine}>{largeFileNoticeText}</p>}
                            </div>
                        )}
                        <SheetWorkbook
                            key={workbookKey}
                            data={dataForWorkbook}
                            workbookRef={workbookRef}
                            customToolbarItems={customToolbarItems}
                            allowEdit={!editLocked}
                            showToolbar={!viewOnly}
                            onOp={handleOp}
                            hooks={hooks}
                        />
                    </div>
                )}
            </BottomDrawer>
            <KeyTipsOverlay open={keytipsOpen} targets={keyTipTargets} announce={keyTipsAnnounce} />
            {/* NameBox/StatusBar/Go To and the Excel-parity dialogs (Format Cells,
                Paste Special, Delete/Insert) are available in every context;
                the close-failed dialog, Rename and History are Workspace-only. */}
            <NameBox
                wrapperRef={wrapperRef}
                workbookRef={workbookRef}
                remountKey={workbookKey}
                workbookMounted={workbookMounted}
                selectionTick={selectionTick}
                onRefocusGrid={focusCellInput}
            />
            <StatusBar
                wrapperRef={wrapperRef}
                workbookRef={workbookRef}
                remountKey={workbookKey}
                workbookMounted={workbookMounted}
                notifyRef={statusBarNotifyRef}
            />
            <GoToDialog open={goToOpen} onClose={() => setGoToOpen(false)} workbookRef={workbookRef} />
            <FormatCellsDialog open={formatCellsOpen} onClose={() => setFormatCellsOpen(false)} workbookRef={workbookRef} />
            <PasteSpecialDialog
                open={pasteSpecial.dialogOpen}
                isInternalSource={pasteSpecial.isInternalSource}
                onClose={pasteSpecial.closeDialog}
                onApply={pasteSpecial.apply}
            />
            {canSave && (
                <ConfirmDialog
                    open={closeFailedOpen}
                    message="Couldn't save your changes."
                    cancelLabel="Close without saving"
                    confirmLabel="Retry"
                    onCancel={handleCloseWithoutSaving}
                    onConfirm={handleRetryCloseFailed}
                    onClose={closeAnyDialog}
                />
            )}
            <ConfirmDialog
                open={deleteInsertMode !== null}
                title={deleteInsertMode === 'insert' ? 'Insert' : 'Delete'}
                confirmLabel="OK"
                cancelLabel="Cancel"
                onConfirm={applyDeleteInsert}
                onClose={closeAnyDialog}
                autoFocusConfirm
                onKeyDown={(e) => {
                    // Excel's Delete/Insert dialog: R / C pick row / column.
                    const key = e.key.toLowerCase();
                    if (e.ctrlKey || e.metaKey || e.altKey || (key !== 'r' && key !== 'c')) return;
                    e.preventDefault();
                    setDeleteInsertAxis(key === 'r' ? 'row' : 'column');
                }}
            >
                <div className={styles.deleteInsertOptions} role="radiogroup" aria-label="Row or column">
                    <RadioButton
                        name="delete-insert-axis"
                        label="Entire row(s)"
                        checked={deleteInsertAxis === 'row'}
                        onChange={(isChecked) => isChecked && setDeleteInsertAxis('row')}
                    />
                    <RadioButton
                        name="delete-insert-axis"
                        label="Entire column(s)"
                        checked={deleteInsertAxis === 'column'}
                        onChange={(isChecked) => isChecked && setDeleteInsertAxis('column')}
                    />
                </div>
            </ConfirmDialog>
            {canSave && (
                <HistorySideDrawer
                    containerId={HISTORY_PORTAL_TARGET_ID}
                    isOpen={historyOpen}
                    isExpanded={historyExpanded}
                    title="Version history"
                    loading={historyLoading}
                    historyEntries={historyEntries}
                    onClose={closeHistoryDrawer}
                    onToggleExpand={toggleHistoryExpand}
                    onSelectEntry={selectHistoryEntry}
                    onPreviewEntry={previewHistoryEntry}
                    selectedEntryId={previewEntryId}
                    previewLoading={historyPreviewLoading}
                    restoreInFooter
                />
            )}
            {!canSave && !viewOnly && <UnsavedEditsDialog {...workspaceGuard.dialog} />}
            {canSave && (
                <RenameReportDialog
                    open={renameOpen}
                    name={displayName}
                    description={currentDescription ?? ''}
                    onClose={() => setRenameOpen(false)}
                    onSave={(newName, newDescription) => {
                        setRenameOpen(false);
                        handleRename(newName, newDescription);
                    }}
                />
            )}
        </>
    );
};

export default SheetEditorDrawer;
